/**
 * Certain® design system v1 — theme management.
 *
 * Three user-pickable values:
 *   - 'light'  — force light
 *   - 'dark'   — force dark
 *   - 'system' — follow the OS preference (default for fresh visits)
 *
 * Persisted in localStorage. The actual <html data-theme="..."> attribute
 * always carries a *resolved* value ('light' or 'dark') so CSS only has to
 * cope with two cases.
 *
 * Call `initTheme()` before React renders so there's no flash of
 * unstyled-by-the-wrong-theme content on first paint.
 */

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'certain-theme';

function resolved(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function apply(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme;
}

export function getTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage can throw in private-mode iframes etc. — fall through.
  }
  return 'system';
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore storage failures — the visual change still applies for this session.
  }
  apply(resolved(theme));
}

/**
 * Initialise theme on app boot. Must run before React paints.
 * Also subscribes to OS-preference changes so a "system" user gets a live
 * update when they flip their OS dark-mode toggle.
 */
export function initTheme(): void {
  const theme = getTheme();
  apply(resolved(theme));

  if (typeof window !== 'undefined' && window.matchMedia) {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (getTheme() === 'system') apply(resolved('system'));
    };
    // Modern browsers
    if (mql.addEventListener) mql.addEventListener('change', handler);
    // Safari < 14 fallback
    else if ((mql as unknown as { addListener?: (cb: () => void) => void }).addListener) {
      (mql as unknown as { addListener: (cb: () => void) => void }).addListener(handler);
    }
  }
}
